import React from 'react';
import { Badge } from '../../Badge';
import { AIBadge } from '../../AiBadge';
import { Button } from 'antd';
import SearchIcon from '../../../icons/SearchIcon';
import { motion } from 'framer-motion';
import { TrustedCompanies } from './TrustedCompanies';

const floatingJobs = [
  {
    title: 'HR Executive',
    className: 'absolute top-[494px] left-[1010px] w-[120px]',
    animation: {
      initial: { opacity: 0, y: 24, scale: 1.1 },
      animate: { opacity: 0.78, y: 0, scale: 1 },
      transition: { duration: 2.26, ease: [0.2, 0.85, 0.25, 1] },
    },
  },
  {
    title: 'Product Manager',
    className: 'absolute top-[134px] left-[70px] w-[140px]',
    animation: {
      initial: { rotate: -45, opacity: 0, scale: 0.88, x: -18, y: 18 },
      animate: { rotate: 0, opacity: 0.72, scale: 1, x: 0, y: 0 },
      transition: { duration: 2.52, ease: [0.2, 0.85, 0.25, 1] },
    },
  },
  {
    title: 'Full Stack Developer',
    className: 'absolute top-[497px] left-[50px] w-[156px]',
    animation: {
      initial: { opacity: 0, scale: 0.5 },
      animate: { opacity: 0.89, scale: 1 },
      transition: { duration: 2.26, ease: [0.2, 0.85, 0.25, 1] },
    },
  },
  {
    title: 'Sale Assistance',
    className: 'absolute top-[58px] left-[1030px] w-[153px]',
    animation: {
      initial: { opacity: 0, rotate: 10 },
      animate: { opacity: 0.84, rotate: 0 },
      transition: { duration: 2.93, ease: 'easeOut' },
    },
  },
];

export const JobSearchHeroSection = (): JSX.Element => {
  return (
    <section className="flex flex-col w-full items-center relative px-4 sm:px-6 lg:px-0">
      <div className="flex flex-col items-start relative self-stretch w-full">
        <div className="flex flex-col items-center justify-center gap-8 sm:gap-10 pt-24 pb-20 sm:pt-28 sm:pb-24 lg:pt-[145px] lg:pb-[159px] lg:px-[113px] relative self-stretch w-full">
          <div className="inline-flex flex-col items-center gap-4 relative w-full max-w-[860px]">
            <div className="flex flex-col items-center gap-2 relative self-stretch w-full">
              <div className="inline-flex items-center gap-3 pl-1 pr-2.5 py-1 relative rounded-2xl">
                <AIBadge title="AI-powered hiring" />
              </div>

              <div className="flex flex-col items-center gap-[10px] relative self-stretch w-full text-center">
                <h1 className="relative flex flex-wrap items-center justify-center self-stretch font-extrabold text-[32px] leading-[40px] sm:text-[40px] sm:leading-[48px] lg:text-[50px] lg:leading-[56px] tracking-[0] [font-family:'Inter',Helvetica] !m-0 !mb-0">
                  <span className="text-[#200342d9]">Find the right</span>
                  <span className="text-[var(--midnight-900)]">&nbsp;</span>
                  <span className="text-primary">job.</span>
                </h1>

                <h2 className="relative flex flex-wrap items-center justify-center self-stretch font-extrabold text-[32px] leading-[40px] sm:text-[40px] sm:leading-[48px] lg:text-[50px] lg:leading-[56px] tracking-[0] [font-family:'Inter',Helvetica] !m-0">
                  <span className="text-[#200342d9]">Hire the right</span>
                  <span className="text-[var(--midnight-900)]">&nbsp;</span>
                  <span className="text-primary">talent.</span>
                </h2>
              </div>
            </div>

            <p className="!m-0 w-full max-w-[684px] px-4 sm:px-0 mt-4 font-['Inter',Helvetica] text-gray-600 text-[20px] leading-[30px] font-light text-center">
              A smarter way to find opportunities and build teams with AI-driven matching,
              automation and insights.
            </p>
          </div>

          <div className="flex flex-col items-stretch sm:flex-row sm:items-center justify-center gap-3 sm:gap-4 relative w-full max-w-[520px]">
            <Button className="group inline-flex items-center justify-center gap-2 px-6 text-base font-semibold text-gray-700 border border-solid border-[#d5d6da] shadow-shadow-xs rounded-lg !h-11 transition-colors hover:text-primary w-full sm:w-auto">
              <SearchIcon className="w-4 h-4 text-gray-700 transition-colors group-hover:text-primary" />
              <span>Search a Job</span>
            </Button>
            <Button
              type="primary"
              className="inline-flex items-center justify-center gap-2 px-6 text-base font-semibold rounded-lg !h-11 w-full sm:w-auto">
              Post a Job
            </Button>
          </div>

          <div className="absolute inset-0 hidden xl:block pointer-events-none">
            {floatingJobs.map((job, index) => (
              <motion.div
                key={job.title}
                className={job.className}
                initial={job.animation.initial}
                animate={job.animation.animate}
                transition={{
                  delay: 0.2 + index * 0.18,
                  ...job.animation.transition,
                }}>
                <Badge className="pointer-events-auto">{job.title}</Badge>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      <TrustedCompanies />
    </section>
  );
};
